// Marketplace B2B API Client
// Uses the same apiGet/apiPost/apiPatch helpers as the rest of the dashboard

import { apiGet, apiPost, apiPatch, apiDelete, apiDownload } from '../../../utils/api';

const B = '/api/marketplace/b2b';

// ─── Dashboard ───────────────────────────────────────────────────────────────
export const fetchDashboardOverview = () => apiGet<any>(`${B}/dashboard/overview`);

// ─── Catalogue ───────────────────────────────────────────────────────────────
export const fetchCatalog = (params?: Record<string, any>) => apiGet<any>(`${B}/catalog`, params || {});
export const createCatalogProduct = (data: any) => apiPost<any>(`${B}/catalog`, data);
export const updateCatalogProduct = (id: number, data: any) => apiPost<any>(`${B}/catalog/${id}`, data);

// ─── Supplier Profiles ──────────────────────────────────────────────────────
export const fetchMySupplierProfile = () => apiGet<any>(`${B}/supplier/profile`);
export const saveSupplierProfile = (data: any) => apiPost<any>(`${B}/supplier/profile`, data);
export const submitSupplierForVerification = () => apiPost<any>(`${B}/supplier/profile/submit`, {});
export const fetchVerifiedSuppliers = () => apiGet<any>(`${B}/supplier/verified`);
export const reviewSupplierVerification = (id: number, decision: string, reason?: string) =>
  apiPost<any>(`${B}/supplier/verification/${id}/review`, { decision, reason });

// ─── Buyer Organisations ────────────────────────────────────────────────────
export const fetchMyBuyerOrganisation = () => apiGet<any>(`${B}/buyer/organisation`);
export const saveBuyerOrganisation = (data: any) => apiPost<any>(`${B}/buyer/organisation`, data);
export const fetchBuyerMembers = () => apiGet<any>(`${B}/buyer/organisation/members`);

// ─── Listings ────────────────────────────────────────────────────────────────
export const browseListings = (params?: Record<string, any>) => apiGet<any>(`${B}/listings`, params || {});
export const fetchListingDetail = (id: number) => apiGet<any>(`${B}/listings/${id}`);
export const fetchMyListings = () => apiGet<any>(`${B}/listings/mine`);
export const createListing = (data: any) => apiPost<any>(`${B}/listings`, data);
export const publishListing = (id: number) => apiPost<any>(`${B}/listings/${id}/publish`, {});
export const pauseListing = (id: number) => apiPost<any>(`${B}/listings/${id}/pause`, {});
export const updateListing = (id: number, formData: FormData | Record<string, any>) => apiPatch<any>(`${B}/listings/${id}`, formData);
export const deleteListing = (id: number) => apiDelete<any>(`${B}/listings/${id}`);
export const resumeListing = (id: number) => apiPost<any>(`${B}/listings/${id}/resume`, {});

// ─── Buyer Orders ────────────────────────────────────────────────────────────
export const createB2bOrder = (data: any) => apiPost<any>(`${B}/orders`, data);
export const fetchB2bOrders = (params?: Record<string, any>) => apiGet<any>(`${B}/orders`, params || {});
export const fetchB2bOrderDetail = (id: number) => apiGet<any>(`${B}/orders/${id}`);
export const transitionB2bOrderStatus = (id: number, status: string) =>
  apiPatch<any>(`${B}/orders/${id}/status`, { status });
export const downloadB2bOrderInvoice = (id: number, orderNumber: string) =>
  apiDownload(`${B}/orders/${id}/invoice.pdf`, `eSanza-${orderNumber}.pdf`);

// ─── Buyer Overview ──────────────────────────────────────────────────────────
export const fetchBuyerOverview = () => apiGet<any>(`${B}/buyer/overview`);

// ─── Buyer Deliveries ────────────────────────────────────────────────────────
export const fetchBuyerDeliveries = (params?: Record<string, any>) =>
  apiGet<any>(`${B}/buyer/deliveries`, params || {});

// ─── Buyer Invoices ──────────────────────────────────────────────────────────
export const fetchBuyerInvoices = (params?: Record<string, any>) =>
  apiGet<any>(`${B}/buyer/invoices`, params || {});
export const downloadBuyerInvoicePdf = (id: number, invoiceNumber: string) =>
  apiDownload(`${B}/buyer/invoices/${id}/pdf`, `${invoiceNumber}.pdf`);

// ─── Buyer Profile ───────────────────────────────────────────────────────────
export const fetchBuyerProfile = () => apiGet<any>(`${B}/buyer/profile`);
export const updateBuyerProfile = (data: any) => apiPost<any>(`${B}/buyer/profile`, data);

// ─── Buyer Quotations ────────────────────────────────────────────────────────
export const fetchBuyerQuotations = (params?: Record<string, any>) =>
  apiGet<any>(`${B}/buyer/quotations`, params || {});
export const fetchBuyerQuotationDetail = (id: number) =>
  apiGet<any>(`${B}/buyer/quotations/${id}`);
export const acceptBuyerQuotation = (id: number) =>
  apiPost<any>(`${B}/buyer/quotations/${id}/accept`, {});
export const rejectBuyerQuotation = (id: number, reason?: string) =>
  apiPost<any>(`${B}/buyer/quotations/${id}/reject`, { reason });

// ─── Supplier Orders ─────────────────────────────────────────────────────────
export const fetchSupplierOrders = (params?: Record<string, any>) =>
  apiGet<any>(`${B}/supplier/orders`, params || {});
export const fetchSupplierOrderDetail = (id: number) => apiGet<any>(`${B}/supplier/orders/${id}`);
export const transitionSupplierOrderStatus = (id: number, status: string, notes?: string) =>
  apiPatch<any>(`${B}/supplier/orders/${id}/status`, { status, notes });
export const downloadSupplierOrderInvoice = (id: number, orderNumber: string) =>
  apiDownload(`${B}/supplier/orders/${id}/invoice.pdf`, `eSanza-${orderNumber}.pdf`);

// ─── RFQs ────────────────────────────────────────────────────────────────────
export const createRfq = (data: any) => apiPost<any>(`${B}/rfqs`, data);
export const fetchRfqs = (params?: Record<string, any>) => apiGet<any>(`${B}/rfqs`, params || {});
export const fetchRfqDetail = (id: number) => apiGet<any>(`${B}/rfqs/${id}`);
export const publishRfq = (id: number) => apiPost<any>(`${B}/rfqs/${id}/publish`, {});

// ─── Quotations ──────────────────────────────────────────────────────────────
export const createQuotation = (data: any) => apiPost<any>(`${B}/quotations`, data);
export const submitQuotation = (id: number) => apiPost<any>(`${B}/quotations/${id}/submit`, {});
export const fetchQuotationsForRfq = (rfqId: number) => apiGet<any>(`${B}/rfqs/${rfqId}/quotations`);
export const acceptQuotation = (id: number) => apiPost<any>(`${B}/quotations/${id}/accept`, {});

// ─── Shipments ───────────────────────────────────────────────────────────────
export const createShipment = (data: any) => apiPost<any>(`${B}/shipments`, data);
export const fetchShipments = (params?: Record<string, any>) => apiGet<any>(`${B}/shipments`, params || {});
export const fetchShipmentDetail = (id: number) => apiGet<any>(`${B}/shipments/${id}`);
export const updateShipmentStatus = (id: number, status: string, exception_notes?: string) =>
  apiPatch<any>(`${B}/shipments/${id}/status`, { status, exception_notes });
export const addShipmentEvidence = (id: number, evidence_type: string, file_url?: string) =>
  apiPost<any>(`${B}/shipments/${id}/evidence`, { evidence_type, file_url });

// ─── Invoices ────────────────────────────────────────────────────────────────
export const createInvoice = (data: any) => apiPost<any>(`${B}/invoices`, data);
export const fetchInvoices = (params?: Record<string, any>) => apiGet<any>(`${B}/invoices`, params || {});
export const recordInvoicePayment = (id: number, amount: number, payment_method: string, reference_number?: string, payment_date?: string) =>
  apiPost<any>(`${B}/invoices/${id}/payment`, { amount, payment_method, reference_number, payment_date });

// ─── Settlements ─────────────────────────────────────────────────────────────
export const createSettlement = (data: any) => apiPost<any>(`${B}/settlements`, data);
export const fetchSettlements = (params?: Record<string, any>) => apiGet<any>(`${B}/settlements`, params || {});

// ─── Commission Rules ────────────────────────────────────────────────────────
export const fetchCommissionRules = () => apiGet<any>(`${B}/commission-rules`);
export const createCommissionRule = (data: any) => apiPost<any>(`${B}/commission-rules`, data);
